import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { format, parseISO } from 'date-fns';
import {
  CurrencyDollarIcon,
  EnvelopeIcon,
  FileTextIcon,
  TruckIcon,
  FilesIcon,
  CaretDownIcon,
  CaretUpIcon,
  ReceiptIcon,
  ShoppingCartIcon,
  InvoiceIcon,
  UploadSimpleIcon,
  PlusIcon,
  LinkBreakIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useArCollectionThreadEvidence,
  useArCollectionThreadDocumentNames,
  useUnlinkArCollectionThreadApInvoiceEvidence,
  useRefreshArCollectionThreadEvidence,
} from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useToast } from 'src/context/ToastContext';
import { getClassificationLabel } from '@/utils/emailClassificationUtils';
import { EvidenceViewerModal } from './EvidenceViewerModal';
import { InboundEmailDetailSheet } from './InboundEmailDetailSheet';
import { OutboundEmailDetailSheet } from '@/components/communications/OutboundEmailDetailSheet';
import { CollectionThreadDocumentUploadDialog } from './CollectionThreadDocumentUploadDialog';
import { LinkApInvoiceDialog } from './LinkApInvoiceDialog';

interface Props {
  threadId: string;
}

function formatMoney(value: string | null): string {
  if (value == null) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  // parseISO over `new Date(...)`: `new Date("2026-04-15")` parses as UTC
  // midnight then shifts to local time, dropping a day in negative offsets.
  // parseISO interprets date-only strings as local midnight, preserving
  // the calendar day across timezones.
  const parsed = parseISO(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, 'MMM d, yyyy');
}

export function CollectionThreadEvidenceSection({ threadId }: Props) {
  const navigate = useNavigate();
  const gt = useGT();
  const { showToast } = useToast();
  const { evidence, isLoading } = useArCollectionThreadEvidence(threadId);
  const { namesByDocumentId } = useArCollectionThreadDocumentNames(threadId);
  const { invoice } = evidence;
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const [openOutboundEmailId, setOpenOutboundEmailId] = useState<string | null>(
    null,
  );
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkApInvoiceOpen, setLinkApInvoiceOpen] = useState(false);
  // Track which row's unlink is in flight so the shared mutation's isPending
  // only disables that row's button, not every AP invoice's.
  const [pendingUnlinkId, setPendingUnlinkId] = useState<string | null>(null);
  const unlinkApInvoice = useUnlinkArCollectionThreadApInvoiceEvidence();
  const refreshEvidence = useRefreshArCollectionThreadEvidence();

  if (isLoading) {
    return (
      <div className="space-y-2 rounded-lg border border-stone-400 dark:border-stone-600 bg-card p-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  const receiptsById = new Map(evidence.receipts.map((r) => [r.id, r]));

  // Inbound (filed) and sent emails interleaved newest-first for the shared
  // "Emails" group.
  const emailRows = [
    ...evidence.inboundEmails.map((email) => ({
      direction: 'inbound' as const,
      date: email.receivedAt,
      email,
    })),
    ...evidence.outboundEmails.map((email) => ({
      direction: 'outbound' as const,
      date: email.sentAt,
      email,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="rounded-lg border border-stone-400 dark:border-stone-600 bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-stone-100 dark:bg-stone-800">
        <span className="text-lg font-semibold text-foreground">
          <T context="section heading, collection thread evidence">Evidence</T>
        </span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => refreshEvidence.mutate({ id: threadId })}
            disabled={refreshEvidence.isPending}
          >
            <ArrowsClockwiseIcon
              className={`h-3.5 w-3.5 mr-1 ${refreshEvidence.isPending ? 'animate-spin' : ''}`}
            />
            <T context="button, re-run the evidence gather for this thread">
              Refresh evidence
            </T>
          </Button>
          <Button
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => setUploadOpen(true)}
          >
            <UploadSimpleIcon className="h-3.5 w-3.5 mr-1" />
            <T context="button, upload files as thread evidence">
              Upload evidence
            </T>
          </Button>
        </div>
      </div>

      <div className="space-y-2 p-3">
        {/* Documents first — the section reps reach for most. */}
        <EvidenceGroup
          icon={<FilesIcon className="h-3.5 w-3.5 text-indigo-600" />}
          title={<T context="evidence group, documents">Documents</T>}
          count={evidence.documents.length}
        >
          {evidence.documents.length === 0 ? (
            <EmptyRow
              text={
                <T context="empty state, no documents linked">
                  No documents linked to this thread yet.
                </T>
              }
            />
          ) : (
            evidence.documents.map((doc) => {
              // Prefer the LLM-generated readable name (cached server-side, may
              // arrive a beat after the evidence list). Fall back to the
              // classification label, then a generic placeholder. When a readable
              // name is shown, keep the classification label on the secondary line
              // so the document type isn't lost.
              const readableName = namesByDocumentId.get(doc.id);
              const classificationLabel = doc.classificationType
                ? getClassificationLabel(doc.classificationType)
                : null;
              return (
                <EvidenceRow
                  key={doc.id}
                  primary={
                    readableName ??
                    classificationLabel ?? (
                      <T context="placeholder, unnamed document">Document</T>
                    )
                  }
                  secondary={[
                    readableName ? classificationLabel : null,
                    doc.originalFileName,
                    formatDate(doc.createdAt),
                  ]}
                  onClick={() => setOpenDocumentId(doc.id)}
                />
              );
            })
          )}
        </EvidenceGroup>

        {invoice && (
          <EvidenceGroup
            icon={<ReceiptIcon className="h-3.5 w-3.5 text-sky-600" />}
            title={<T context="evidence group, AR invoice">Invoice</T>}
            count={1}
          >
            <EvidenceRow
              primary={
                invoice.invoiceNumber ? (
                  <>
                    <T>Invoice</T> <Var>{invoice.invoiceNumber}</Var>
                  </>
                ) : (
                  `Invoice #${invoice.internalId}`
                )
              }
              secondary={[
                invoice.grossAmount ? formatMoney(invoice.grossAmount) : null,
                invoice.balanceDue
                  ? `Balance ${formatMoney(invoice.balanceDue)}`
                  : null,
                invoice.dueDate ? `Due ${formatDate(invoice.dueDate)}` : null,
              ]}
              status={invoice.status}
              onClick={() =>
                navigate({
                  to: '/accounts-receivable/invoices/$arInvoiceId',
                  params: { arInvoiceId: invoice.id },
                })
              }
            />
          </EvidenceGroup>
        )}

        {evidence.customerPurchaseOrder && (
          <EvidenceGroup
            icon={<ShoppingCartIcon className="h-3.5 w-3.5 text-violet-600" />}
            title={
              <T context="evidence group, customer purchase order">
                Customer PO
              </T>
            }
            count={1}
          >
            <EvidenceRow
              primary={`PO ${evidence.customerPurchaseOrder.purchaseOrderNumber}`}
              secondary={[
                evidence.customerPurchaseOrder.totalAmount
                  ? formatMoney(evidence.customerPurchaseOrder.totalAmount)
                  : null,
                formatDate(evidence.customerPurchaseOrder.orderDate),
              ]}
              status={evidence.customerPurchaseOrder.status}
            />
          </EvidenceGroup>
        )}

        <EvidenceGroup
          icon={<InvoiceIcon className="h-3.5 w-3.5 text-teal-600" />}
          title={<T context="evidence group, AP invoices">AP invoices</T>}
          count={evidence.apInvoices.length}
          action={
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              onClick={() => setLinkApInvoiceOpen(true)}
            >
              <PlusIcon className="h-3.5 w-3.5 mr-1" />
              <T context="button, manually link an AP invoice as thread evidence">
                Link
              </T>
            </Button>
          }
        >
          {evidence.apInvoices.length === 0 ? (
            <EmptyRow
              text={
                <T context="empty state, no AP invoices linked">
                  No AP invoices linked to this thread yet.
                </T>
              }
            />
          ) : (
            evidence.apInvoices.map((inv) => (
              <EvidenceRow
                key={inv.id}
                primary={
                  inv.invoiceNumber ? (
                    <>
                      <T>Invoice</T> <Var>{inv.invoiceNumber}</Var>
                    </>
                  ) : (
                    <T context="placeholder, AP invoice without number">
                      AP invoice
                    </T>
                  )
                }
                secondary={[
                  inv.supplierName,
                  inv.totalAmount ? formatMoney(inv.totalAmount) : null,
                  formatDate(inv.invoiceDate),
                ]}
                status={inv.status}
                onClick={() =>
                  navigate({
                    to: '/accounts-payable/invoices/$invoiceId',
                    params: { invoiceId: inv.id },
                  })
                }
                // Only manually-linked invoices are removable; gather/document-
                // derived ones aren't directly unlinkable.
                onUnlink={
                  inv.manuallyLinked
                    ? () => {
                        setPendingUnlinkId(inv.id);
                        unlinkApInvoice.mutate(
                          { id: threadId, apInvoiceId: inv.id },
                          {
                            onError: () =>
                              showToast({
                                title: gt('Unlink failed', {
                                  context:
                                    'toast title, failed to unlink AP invoice evidence',
                                }),
                                message: gt(
                                  'Could not remove this invoice. Please try again.',
                                  {
                                    context:
                                      'toast message, failed to unlink AP invoice evidence',
                                  },
                                ),
                                color: 'error',
                              }),
                            onSettled: () => setPendingUnlinkId(null),
                          },
                        );
                      }
                    : undefined
                }
                unlinkPending={
                  unlinkApInvoice.isPending && pendingUnlinkId === inv.id
                }
              />
            ))
          )}
        </EvidenceGroup>

        {evidence.paymentApplications.length > 0 && (
          <EvidenceGroup
            icon={<CurrencyDollarIcon className="h-3.5 w-3.5 text-green-600" />}
            title={
              <T context="evidence group, payments applied to invoice">
                Payments
              </T>
            }
            count={evidence.paymentApplications.length}
            maxBodyHeight="20rem"
          >
            {evidence.paymentApplications.map((pa) => {
              // The applicationDate is when the AR clerk applied the cash; the
              // receipt's own date is more useful here (when it landed).
              const receipt = pa.bankTransactionId
                ? receiptsById.get(pa.bankTransactionId)
                : undefined;
              const dateRaw = receipt?.transactionDate ?? pa.applicationDate;
              return (
                <EvidenceRow
                  key={pa.id}
                  primary={formatMoney(pa.appliedAmount)}
                  secondary={[
                    formatDate(dateRaw),
                    receipt?.referenceNumber
                      ? `Ref ${receipt.referenceNumber}`
                      : null,
                    receipt?.paymentType ?? null,
                  ]}
                />
              );
            })}
          </EvidenceGroup>
        )}

        {evidence.remittanceAdvices.length > 0 && (
          <EvidenceGroup
            icon={<FileTextIcon className="h-3.5 w-3.5 text-blue-600" />}
            title={
              <T context="evidence group, remittance advices">
                Remittance advices
              </T>
            }
            count={evidence.remittanceAdvices.length}
          >
            {evidence.remittanceAdvices.map((ra) => (
              <EvidenceRow
                key={ra.id}
                primary={ra.statedCustomerName ?? <T>Remittance advice</T>}
                secondary={[
                  ra.statedTotalAmount
                    ? formatMoney(ra.statedTotalAmount)
                    : null,
                  ra.statedReferenceNo ? `Ref ${ra.statedReferenceNo}` : null,
                  formatDate(ra.statedPaymentDate ?? ra.receivedAt),
                ]}
                status={ra.channel}
                onClick={() =>
                  navigate({
                    to: '/accounts-receivable/remittances/$remittanceId',
                    params: { remittanceId: ra.id },
                  })
                }
              />
            ))}
          </EvidenceGroup>
        )}

        <EvidenceGroup
          icon={<EnvelopeIcon className="h-3.5 w-3.5 text-orange-600" />}
          title={<T context="evidence group, emails">Emails</T>}
          count={emailRows.length}
        >
          {emailRows.length === 0 ? (
            <EmptyRow
              text={
                <T context="empty state, no emails on the thread">
                  No emails on this thread yet.
                </T>
              }
            />
          ) : (
            emailRows.map((row) =>
              row.direction === 'inbound' ? (
                <EvidenceRow
                  key={`inbound-${row.email.id}`}
                  primary={
                    row.email.subject ?? (
                      <T context="placeholder, no subject">(no subject)</T>
                    )
                  }
                  secondary={[
                    row.email.from ? `From ${row.email.from}` : null,
                    formatDate(row.email.receivedAt),
                  ]}
                  badges={row.email.carriedDocumentClassifications.map(
                    getClassificationLabel,
                  )}
                  onClick={() => setOpenEmailId(row.email.id)}
                />
              ) : (
                <EvidenceRow
                  key={`outbound-${row.email.id}`}
                  primary={
                    row.email.subject || (
                      <T context="placeholder, no subject">(no subject)</T>
                    )
                  }
                  secondary={[
                    `To ${row.email.to}`,
                    formatDate(row.email.sentAt),
                  ]}
                  status="sent"
                  onClick={() => setOpenOutboundEmailId(row.email.id)}
                />
              ),
            )
          )}
        </EvidenceGroup>

        {evidence.shipments.length > 0 && (
          <EvidenceGroup
            icon={<TruckIcon className="h-3.5 w-3.5 text-amber-600" />}
            title={<T context="evidence group, shipments">Shipments</T>}
            count={evidence.shipments.length}
          >
            {evidence.shipments.map((shipment) => (
              <EvidenceRow
                key={shipment.id}
                primary={
                  shipment.trackingReference ? (
                    <>
                      <T>Tracking</T> <Var>{shipment.trackingReference}</Var>
                    </>
                  ) : (
                    <T context="placeholder, shipment without tracking">
                      Shipment
                    </T>
                  )
                }
                secondary={[
                  shipment.carrier ?? null,
                  shipment.shipDate
                    ? `Shipped ${formatDate(shipment.shipDate)}`
                    : null,
                  shipment.deliveryDate
                    ? `Delivered ${formatDate(shipment.deliveryDate)}`
                    : null,
                ]}
                status={shipment.status}
              />
            ))}
          </EvidenceGroup>
        )}
      </div>

      <InboundEmailDetailSheet
        inboundEmailId={openEmailId}
        onClose={() => setOpenEmailId(null)}
      />
      <OutboundEmailDetailSheet
        outboundEmailId={openOutboundEmailId}
        onClose={() => setOpenOutboundEmailId(null)}
      />
      <EvidenceViewerModal
        documents={evidence.documents}
        namesByDocumentId={namesByDocumentId}
        openDocumentId={openDocumentId}
        onClose={() => setOpenDocumentId(null)}
        onNavigate={setOpenDocumentId}
      />
      <CollectionThreadDocumentUploadDialog
        threadId={threadId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
      <LinkApInvoiceDialog
        threadId={threadId}
        open={linkApInvoiceOpen}
        onOpenChange={setLinkApInvoiceOpen}
      />
    </div>
  );
}

interface EvidenceGroupProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  count: number;
  children: React.ReactNode;
  /** When set, caps the list height and scrolls overflow. */
  maxBodyHeight?: string;
  /** Optional header action (e.g. an upload button), rendered before the caret. */
  action?: React.ReactNode;
}

function EvidenceGroup({
  icon,
  title,
  count,
  children,
  maxBodyHeight,
  action,
}: EvidenceGroupProps) {
  const gt = useGT();
  const [open, setOpen] = useState(true);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border bg-card overflow-hidden">
        {/* Header row: the title and caret each toggle the section. The action
            sits between them as its own control (not nested inside a button). */}
        <div className="flex items-center gap-1 pr-2 hover:bg-muted/30 transition-colors">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex flex-1 items-center gap-2 px-3 py-2 text-left"
            >
              {icon}
              <span className="text-xs font-semibold text-foreground">
                {title}
              </span>
              <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                {count}
              </Badge>
            </button>
          </CollapsibleTrigger>
          {action}
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-label={gt('Toggle section', {
                context: 'aria-label, expand or collapse an evidence group',
              })}
              className="p-1 text-muted-foreground"
            >
              {open ? (
                <CaretUpIcon className="h-3 w-3" />
              ) : (
                <CaretDownIcon className="h-3 w-3" />
              )}
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div
            className={maxBodyHeight ? 'overflow-y-auto border-t' : undefined}
            style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}
          >
            <ul className={maxBodyHeight ? 'divide-y' : 'divide-y border-t'}>
              {children}
            </ul>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

interface EvidenceRowProps {
  primary: React.ReactNode;
  secondary?: Array<string | null>;
  status?: string | null;
  /** Extra labels rendered as outline badges (e.g. carried document types). */
  badges?: string[];
  onClick?: () => void;
  /** When set, renders an unlink button alongside the row (outside its click area). */
  onUnlink?: () => void;
  /** Disables the unlink button while a removal is in flight. */
  unlinkPending?: boolean;
}

function EvidenceRow({
  primary,
  secondary,
  status,
  badges,
  onClick,
  onUnlink,
  unlinkPending,
}: EvidenceRowProps) {
  const gt = useGT();
  const tags = (secondary ?? []).filter(
    (s): s is string => typeof s === 'string' && s.length > 0,
  );
  const body = (
    <>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{primary}</div>
        {tags.length > 0 && (
          <div className="text-xs text-muted-foreground truncate">
            {tags.join(' · ')}
          </div>
        )}
      </div>
      {badges?.map((badge) => (
        <Badge key={badge} variant="outline" className="text-[10px] shrink-0">
          {badge}
        </Badge>
      ))}
      {status && (
        <Badge variant="secondary" className="capitalize text-[10px]">
          {status.replace(/_/g, ' ')}
        </Badge>
      )}
    </>
  );

  // The unlink control can't live inside the row's click <button> (nested
  // buttons are invalid), so when present the row becomes a flex container with
  // the clickable area and the unlink button as siblings — mirroring how
  // EvidenceGroup composes its header action.
  const unlinkButton = onUnlink ? (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 w-7 p-0 shrink-0 text-muted-foreground"
      disabled={unlinkPending}
      onClick={onUnlink}
      aria-label={gt('Unlink', {
        context: 'aria-label, remove manually-linked evidence from thread',
      })}
    >
      <LinkBreakIcon className="h-3.5 w-3.5" />
    </Button>
  ) : null;

  if (onClick) {
    return (
      <li className="flex items-center pr-2">
        <button
          type="button"
          onClick={onClick}
          className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left cursor-pointer hover:bg-muted/40 transition-colors"
        >
          {body}
        </button>
        {unlinkButton}
      </li>
    );
  }
  return (
    <li className="flex items-center gap-2 px-3 py-2">
      {body}
      {unlinkButton}
    </li>
  );
}

function EmptyRow({ text }: { text: React.ReactNode }) {
  return (
    <li className="px-3 py-2 text-xs text-muted-foreground italic">{text}</li>
  );
}
